import { useState, useEffect } from "react";
import axios from "axios";
import { DatePicker, TimePicker, Checkbox, Button, Card, Row, Col, Typography, App, } from "antd";
import { LeftOutlined, RightOutlined, CalendarOutlined, ClockCircleOutlined, ClearOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import "./SetWorkingShift.scss";
import { daysOfWeek } from "../dayOfWeek";
import { URL } from "../../../constant/url";
import { useStore } from "../../../components/globalstate/GlobalState";

const { Title, Text } = Typography;


const SetWorkingShift = ({ update = [], setData }) => {
  const [week, setWeek] = useState(dayjs().startOf("week").add(1, "day"));
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [scheduleData, setScheduleData] = useState([]);
  const { message } = App.useApp();

  const location = useStore((state) => state.location) || localStorage.getItem("selectedLocation");


  const isPastDate = (date) =>
    dayjs(date, "MM/DD/YYYY").isBefore(dayjs(), "day");

  // Helper: normalize data to 7 days
  const normalizeSchedule = (dataArray = []) => {
    return daysOfWeek.map((day, idx) => {
      const found = dataArray.find((d) => d.day === day);
      return {
        day,
        date: found?.date || dayjs().startOf("week").add(idx + 1, "day").format("MM/DD/YYYY"),
        name: localStorage.getItem("cashier"),
        isWorkingDay: found?.morningShift?.[0]?.isWorkingDay || found?.eveningShift?.[0]?.isWorkingDay,
        morningStart: found?.morningShift?.[0]?.startTime || null,
        morningEnd: found?.morningShift?.[0]?.endTime || null,
        eveningStart: found?.eveningShift?.[0]?.startTime || null,
        eveningEnd: found?.eveningShift?.[0]?.endTime || null,
      };
    });
  };

  // Initialize scheduleData from update prop
  useEffect(() => {
    setScheduleData(normalizeSchedule(update));
  }, [update]);

  // Fetch shifts from backend
  const fetchShifts = async () => {
    try {
      const response = await fetch(
        `${URL}/api/workingshift/list`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ week: week.format("YYYY-MM-DD"), location }),
        }
      );

      const data = await response.json();
      if (response.ok) {
        const normalized = normalizeSchedule(data.schedule);
        setScheduleData(normalized);
        setData(data.schedule || []);
      } else {
        setScheduleData(normalizeSchedule());
      }
    } catch (err) {
      console.error("Fetch error:", err);
      setScheduleData(normalizeSchedule());
    }
  };

  useEffect(() => {
    fetchShifts();
  }, [week]);

  // Duration calculation
  const calculateDuration = (start, end) => {
    if (!start || !end) return 0;
    const duration = end.diff(start, "hour", true);
    return Math.max(0, duration);
  };

  // Handle time change
  const handleTimeChange = (dayIndex, field, time) => {
    setScheduleData((prev) =>
      prev.map((day, idx) =>
        idx === dayIndex ? { ...day, [field]: time ? time.format("HH:mm") : null } : day
      )
    );
  };

  // Handle working day toggle
  const handleWorkingDayChange = async (dayIndex, checked) => {
    const day = scheduleData[dayIndex];


    if (!checked && day.name) {
      try {
        await axios.delete(`${URL}/api/deleteworkingshift`, {
          data: { shiftData: { ...day, location } },
        });
        message.success("Shift deleted successfully");
        fetchShifts(); // Refresh the schedule after deletion
      } catch (error) {
        console.error(error);
        message.error("Failed to delete shift");
      }
    }

    setScheduleData((prev) =>
      prev.map((d, idx) =>
        idx === dayIndex
          ? {
            ...d,
            isWorkingDay: checked,
            startTime: checked ? d.startTime : null,
            endTime: checked ? d.endTime : null,
          }
          : d
      )
    );
  };

  const handleClearShift = async (dayIndex, shiftType) => {
    const day = scheduleData[dayIndex];

    // Map shiftType to state keys
    const shiftKeys = {
      morningShift: { start: "morningStart", end: "morningEnd" },
      eveningShift: { start: "eveningStart", end: "eveningEnd" },
    };

    if (!day.name || !shiftKeys[shiftType]) return;

    try {
      // Delete shift from backend
      await axios.delete(`${URL}/api/deletesingleworkingshift`, {
        data: {
          shiftData: {
            date: day.date,
            name: day.name,
            shiftType,
            location,
          },
        },
      });
      message.success("Shift deleted successfully");
      fetchShifts(); // Refresh schedule after deletion
    } catch (error) {
      console.error(error);
      message.error("Failed to delete shift");
    }

    // Clear shift times in local state
    setScheduleData((prev) =>
      prev.map((d, idx) => {
        if (idx !== dayIndex) return d;

        const keys = shiftKeys[shiftType];
        return {
          ...d,
          [keys.start]: null,
          [keys.end]: null,
          isWorkingDay: true,
        };
      })
    );
  };


  const handleSubmitShift = async (dayIndex, shiftType) => {
    const day = scheduleData[dayIndex];
    const shiftKeys = {
      morningShift: { start: "morningStart", end: "morningEnd" },
      eveningShift: { start: "eveningStart", end: "eveningEnd" },
    };



    if (!day.name || !shiftKeys[shiftType]) return;

    const keys = shiftKeys[shiftType];
    const startTime = day[keys.start];
    const endTime = day[keys.end];
    if (!startTime || !endTime) {
      message.error("Please select both start and end time.");
      return;
    }

    const shiftData = {
      startTime,
      endTime,
      role: day.role || "user",
      name: day.name,
      isWorkingDay: day.isWorkingDay,
      shiftType,
    };

    try {
      await axios.put(`${URL}/api/updateworkingshift`, {
        ...shiftData,
        date: dayjs(day.date).format("MM/DD/YYYY"),
        name: day.name,
        location,
      });
      message.success("Shift updated successfully");
      fetchShifts();
    } catch (error) {
      console.error(error);
      message.error("Failed to update shift");
    }
  };



  // Week navigation
  const navigateWeek = (direction) => {
    const newDate = selectedDate.add(direction * 7, "day");
    setSelectedDate(newDate);
    setWeek(newDate.startOf("week").add(1, "day"));
  };

  const goThisWeek = () => {
    const today = dayjs();
    setSelectedDate(today);
    setWeek(today.startOf("week").add(1, "day"));
  };



  const disableEvening = () => ({
    disabledHours: () =>
      [...Array(24).keys()].filter(h => h < 13 || h > 22),
  });


  const disableMorning = () => ({
    disabledHours: () =>
      [...Array(24).keys()].filter(h => h < 5 || h > 13),
  });


  return (
    <div className="work-schedule">
      <div className="schedule-header">
        <div className="week-navigation">
          <Button
            type="text"
            icon={<LeftOutlined />}
            onClick={() => navigateWeek(-1)}
            className="nav-button"
          >
            Prev
          </Button>

          <Button type="default" className="week-button" onClick={goThisWeek}>
            <CalendarOutlined />
            This Week
          </Button>

          <Button
            type="text"
            icon={<RightOutlined />}
            onClick={() => navigateWeek(1)}
            className="nav-button"
          >
            Next
          </Button>
        </div>

        <DatePicker
          value={selectedDate}
          onChange={(date) => {
            setSelectedDate(date);
            setWeek(date.startOf("week").add(1, "day"));
          }}
          format="MM/DD/YYYY"
          className="date-picker"
        />
      </div>

      {/* Schedule Content */}
      <div className="schedule-content">
        {scheduleData.map((dayData, index) => {
          const disabled = isPastDate(dayData.date);
          return (
            <Card key={dayData.day} className="day-card">
              <div className="day-header">
                <div className="day-info">
                  <Title level={4} className="day-title">{dayData.day}</Title>
                  <Text className="day-date">{dayData.date}</Text>
                </div>
                <Checkbox
                  checked={dayData.isWorkingDay}
                  onChange={(e) => handleWorkingDayChange(index, e.target.checked)}
                  disabled={disabled}
                >
                  Working Day
                </Checkbox>
              </div>

              {dayData.isWorkingDay && (
                <Row gutter={24} className="shift-row">
                  {/* Morning Shift */}
                  <Col span={12}>
                  <div className="shift-section morning-shift">
                    <Text strong className="shift-title">🌅 Morning Shift</Text>
                    <Row gutter={12} className="time-inputs">
                      <Col span={12}>
                        <TimePicker
                          value={dayData.morningStart ? dayjs(dayData.morningStart, "HH:mm") : null}
                          onChange={(time) => handleTimeChange(index, "morningStart", time)}
                          format="HH:mm"
                          suffixIcon={<ClockCircleOutlined />}
                          placeholder="Start"
                          disabled={disabled}
                          disabledTime={disableMorning}
                        />
                      </Col>
                      <Col span={12}>
                        <TimePicker
                          value={dayData.morningEnd ? dayjs(dayData.morningEnd, "HH:mm") : null}
                          onChange={(time) => handleTimeChange(index, "morningEnd", time)}
                          format="HH:mm"
                          suffixIcon={<ClockCircleOutlined />}
                          placeholder="End"
                          disabledTime={disableMorning}
                        />
                      </Col>
                    </Row>
                    <Text className="duration">
                      Duration: {calculateDuration(
                        dayData.morningStart ? dayjs(dayData.morningStart, "HH:mm") : null,
                        dayData.morningEnd ? dayjs(dayData.morningEnd, "HH:mm") : null
                      ).toFixed(1)} hours
                    </Text>
                    <div className="shift-actions">
                      <Button disabled={disabled} type="primary" size="small" onClick={() => handleSubmitShift(index, "morningShift")}>Submit</Button>
                      <Button disabled={disabled} type="default" size="small" icon={<ClearOutlined />} onClick={() => handleClearShift(index, "morningShift")}>Clear</Button>
                    </div>
                  </div>
                  </Col>

                  {/* Evening Shift */}
                  <Col span={12}>
                  <div className="shift-section evening-shift">
                    <Text strong className="shift-title">🌙 Evening Shift</Text>
                    <Row gutter={12} className="time-inputs">
                      <Col span={12}>
                        <TimePicker
                          value={dayData.eveningStart ? dayjs(dayData.eveningStart, "HH:mm") : null}
                          onChange={(time) => handleTimeChange(index, "eveningStart", time)}
                          format="HH:mm"
                          suffixIcon={<ClockCircleOutlined />}
                          placeholder="Start"
                          disabled={disabled}
                          disabledTime={disableEvening}
                        />
                      </Col>
                      <Col span={12}>
                        <TimePicker
                          value={dayData.eveningEnd ? dayjs(dayData.eveningEnd, "HH:mm") : null}
                          onChange={(time) => handleTimeChange(index, "eveningEnd", time)}
                          format="HH:mm"
                          suffixIcon={<ClockCircleOutlined />}
                          placeholder="End"
                          disabled={disabled}
                          disabledTime={disableEvening  }
                        />
                      </Col>
                    </Row>
                    <Text className="duration">
                      Duration: {calculateDuration(
                        dayData.eveningStart ? dayjs(dayData.eveningStart, "HH:mm") : null,
                        dayData.eveningEnd ? dayjs(dayData.eveningEnd, "HH:mm") : null
                      ).toFixed(1)} hours
                    </Text>
                    <div className="shift-actions">
                      <Button disabled={disabled} type="primary" size="small" onClick={() => handleSubmitShift(index, "eveningShift")}>Submit</Button>
                      <Button disabled={disabled} type="default" size="small" icon={<ClearOutlined />} onClick={() => handleClearShift(index, "eveningShift")}>Clear</Button>
                    </div>
                  </div>
                  </Col>
                </Row>
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default SetWorkingShift;
